import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import { useInspectorStore } from '@/stores/inspectorStore';

/**
 * Keep the side-peek inspector below a page's toolbar row.
 *
 * The side-peek `InspectorPanel` is absolutely positioned over the layout's
 * whole content region, so by default it covers everything in the routed
 * view — including a page's toolbar (entity picker, filters, paginator).
 * Pages that keep a persistent toolbar at the top pass its element ref here;
 * the toolbar's rendered height is pushed into `inspectorStore.topOffset`,
 * which the panel uses as its `top`, leaving the toolbar visible and
 * clickable while the inspector is open.
 *
 * A ResizeObserver tracks the toolbar as it wraps/reflows (window resizes,
 * filter chips growing the row, etc.). The offset resets to 0 on unmount so
 * pages without a toolbar get the full-height panel again.
 *
 * This assumes the toolbar sits flush at the top of the routed view — true
 * for the search / track-matching style pages this exists for. If a page
 * ever puts content above its toolbar, this needs to measure the toolbar's
 * bottom edge relative to the layout content region instead.
 */
export function useInspectorTopOffset(toolbarRef: Ref<HTMLElement | null>): void {
    const inspectorStore = useInspectorStore();

    let observer: ResizeObserver | null = null;

    function measure(el: HTMLElement) {
        inspectorStore.setTopOffset(el.offsetHeight);
    }

    function observe(el: HTMLElement | null) {
        observer?.disconnect();
        if (!el) {
            inspectorStore.setTopOffset(0);
            return;
        }
        measure(el);
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => measure(el));
            observer.observe(el);
        }
    }

    onMounted(() => {
        observe(toolbarRef.value);
        // Re-attach if the toolbar element itself is swapped (v-if remounts).
        watch(toolbarRef, (el) => observe(el));
    });

    onBeforeUnmount(() => {
        observer?.disconnect();
        observer = null;
        inspectorStore.setTopOffset(0);
    });
}
